import { action, computed, makeObservable, observable } from 'mobx'
import { getValue, isPending } from 'mobx-async'

import {
  AvailableMembershipPlan,
  getAvailableMembershipPlans,
} from '@arriere/modules/membership'

class CreateBookingMembershipStore {
  private userId?: string
  private isNewGuest = false
  hasActiveMembership = false
  selectedMembershipPlanId: string | null = null

  constructor() {
    makeObservable<
      CreateBookingMembershipStore,
      'userId' | 'isNewGuest' | 'availablePlansPromise'
    >(this, {
      userId: observable,
      isNewGuest: observable,
      hasActiveMembership: observable,
      selectedMembershipPlanId: observable,
      availablePlansPromise: computed,
      availablePlans: computed,
      isLoadingPlans: computed,
      selectedPlan: computed,
      shouldShowPicker: computed,
      hasAvailablePlans: computed,
      setGuest: action.bound,
      setIsNewGuest: action.bound,
      setSelectedMembershipPlanId: action.bound,
      reset: action.bound,
    })
  }

  private get availablePlansPromise(): Promise<AvailableMembershipPlan[]> {
    if (this.hasActiveMembership) return Promise.resolve([])
    if (this.userId) return getAvailableMembershipPlans(this.userId)
    if (this.isNewGuest) return getAvailableMembershipPlans()
    return Promise.resolve([])
  }

  get availablePlans(): AvailableMembershipPlan[] {
    return getValue(this.availablePlansPromise) ?? []
  }

  get isLoadingPlans(): boolean {
    return isPending(this.availablePlansPromise)
  }

  get selectedPlan(): AvailableMembershipPlan | null {
    if (!this.selectedMembershipPlanId) return null
    return this.availablePlans.find(plan => plan.id === this.selectedMembershipPlanId) ?? null
  }

  get shouldShowPicker(): boolean {
    return (Boolean(this.userId) || this.isNewGuest) && !this.hasActiveMembership
  }

  get hasAvailablePlans(): boolean {
    return this.availablePlans.length > 0
  }

  setGuest(userId: string | undefined, hasActiveMembership: boolean) {
    if (this.userId !== userId || hasActiveMembership) {
      this.selectedMembershipPlanId = null
    }
    this.userId = userId
    this.isNewGuest = false
    this.hasActiveMembership = hasActiveMembership
  }

  setIsNewGuest(isNewGuest: boolean) {
    if (this.isNewGuest === isNewGuest) return
    this.isNewGuest = isNewGuest
    this.userId = undefined
    this.hasActiveMembership = false
    this.selectedMembershipPlanId = null
  }

  setSelectedMembershipPlanId(planId: string | null) {
    this.selectedMembershipPlanId = planId
  }

  reset() {
    this.userId = undefined
    this.isNewGuest = false
    this.hasActiveMembership = false
    this.selectedMembershipPlanId = null
  }
}

export const createBookingMembershipStore = new CreateBookingMembershipStore()
